import { inject } from "vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { EmailForm } from "@/types/emails/EmailForms";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import { BulkEmailResponse } from "@/types/emails/BulkEmailResponse";
import { EmailTemplateFile } from "@/types/EmailTemplateFile";
import { CorrespondenceFile } from "@/types/CorrespondenceFile";
import { EmailTemplate } from "@/types/EmailTemplate";
import { EmailSignatures } from "acquaint-api/models/EmailSignatures";
import { UserDetails } from "@/types/users/UserDetails";
import { ContactSummary } from "@/types/contacts/ContactSummary";
import { Body } from "@/types/generic/Body";
import { PropertyBulletinPreview } from "@/types/emails/PropertyBulletinPreview";
import { EmailPreviewFields } from "@/types/emails/EmailPreviewFields";

export function useEmailHelper() {
  const apiClient = inject<AcquaintApiClient>("AcquaintApiClient")!;
  const apiNextClient = inject<AcquaintApiNextClient>("AcquaintApiNextClient")!;

  return {
    sendAddBulkEmails: async (emailFormsToSubmit: EmailForm[]) => {
      try {
        return await apiNextClient.httpPost<BulkEmailResponse>(
          "bulkemail/AddBulkEmails",
          emailFormsToSubmit,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    callAttachmentsApi: async (emailId: number, filesToSubmit: FormData) => {
      try {
        await apiNextClient.sendFormData(
          `bulkemail/${emailId}/UploadBulkEmailAttachments`,
          filesToSubmit,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchForwardedEmailAttachmentsList: async (
      emailToForwardId: number,
    ): Promise<EmailTemplateFile[]> => {
      try {
        return await apiNextClient.httpGet(
          `email/${emailToForwardId}/contactemailattachments`,
        );
      } catch (err) {
        displayErrorMessage("The attachments list could not be fetched.");
        return [];
      }
    },

    fetchCorrespondenceFile: async (fileId: number) => {
      try {
        return await apiNextClient.httpGet<CorrespondenceFile>(
          `ContactDocument/${fileId}`,
        );
      } catch (err) {
        displayErrorMessage("The correspondence file could not be fetched.");
        return;
      }
    },

    fetchEmailAttachmentsList: async (emailTemplateId: number) => {
      try {
        return await apiNextClient.httpGet<EmailTemplateFile[]>(
          `email/${emailTemplateId}/emailattachments`,
        );
      } catch (err) {
        displayErrorMessage(
          "Unable to retrieve attachments for the selected template. To resolve this please check the Email Template configuration. You are still able to send this email.",
        );
        return [];
      }
    },

    getEmailCredits: async () => {
      try {
        return await apiNextClient.httpGet<number>(
          "/Email/getemailcreditbalance",
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchEmailTemplateData: async (emailTemplateId: number) => {
      try {
        return await apiNextClient.httpGet<EmailTemplate>(
          `EmailTemplates/${emailTemplateId}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchEmailSignature: async (emailSignatureId: number) => {
      try {
        return await apiNextClient.httpGet<EmailSignatures>(
          `EmailSignatures/${emailSignatureId}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchUserDetails: async (userId: number) => {
      try {
        return await apiClient.httpGet<UserDetails>(
          `app/user/record/${userId}`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchAzureEmailFromAddress: async () => {
      try {
        return await apiNextClient.httpGet<string>(
          "/Email/GetAzureEmailFromAddress",
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchSendToContactData: async (id: number) => {
      try {
        return await apiClient.httpGet<ContactSummary>(
          `app/contacts/${id}/summary`,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
      }
    },

    fetchNewsLetterPreviewBody: async (templateId: number, body: string) => {
      try {
        return await apiClient.httpPost<Body>(
          "app/correspondence/email/newsletter",
          { templateId, body },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return { body: "" };
      }
    },

    fetchPropertyDetails: async (
      templateId: number,
      propertyId: number,
      body: string,
    ) => {
      try {
        return await apiClient.httpPost<Body>(
          "app/correspondence/email/propertydetails",
          { templateId, propertyId, body },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return { body: "" };
      }
    },

    fetchPropertyBulletinText: async (
      contactId: number,
      templateId: number,
      includeValuations: boolean,
      includeInstructed: boolean,
      body: string,
    ) => {
      try {
        return await apiClient.httpPost<PropertyBulletinPreview>(
          `app/correspondence/email/propertybulletin`,
          {
            contactId,
            templateId,
            includeValuations,
            includeInstructed,
            body,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return { body: "", noPropertiesFound: false };
      }
    },

    previewMergefields: async (contactId: number, emailForm: EmailForm) => {
      try {
        return await apiClient.httpPost<EmailPreviewFields>(
          `app/correspondence/email/preview/mergefields`,
          {
            subject: emailForm.subject,
            body: emailForm.body,
            contactId: contactId,
            propertyId: emailForm.propertyId,
            propertyOfferId: emailForm.propertyOfferId,
            appointmentId: emailForm.appointmentId,
            workOrderId: emailForm.workOrderId,
            certificateId: emailForm.certificateId,
            tenancyId: emailForm.tenancyId,
            propertyFeedbackContactId: emailForm.propertyFeedbackContactId,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return {
          subject: "",
          body: "",
        };
      }
    },

    getEmailAttachment: async (id: number) => {
      try {
        return await apiNextClient.httpGet<EmailTemplateFile>(
          `email/${id}/emailattachment`,
        );
      } catch (err) {
        displayErrorMessage("The attachment could not be downloaded.");
      }
    },
  };
}
